import type { ListBuilderConfiguration } from '../types';

/**
 * Shared value mapping for agent types
 */
const AGENT_TYPE_VALUE_MAPPING = [
	{ value: 'order_status' },
	{ value: 'ar_collections' },
	{ value: 'sales_assistant' },
	{ value: 'customer_service' },
	{ value: 'reorder_reminder' },
	{ value: 'emergency_response' },
	{ value: 'lead_qualification' },
];

/**
 * Conversation Agent list configuration
 * Shows AI conversation agents in the system
 * Converted from JSON to TypeScript for type safety
 */
export const CONVERSATION_AGENT_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-agents-all',
			cacheTTL: 600000,
			name: 'All Agents',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'avatar',
					width: 60,
					minWidth: 60,
					maxWidth: 60,
					fields: [
						{
							id: 'avatar-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: '_conversation_agent',
								},
							},
							customComponent: { id: 'conversation-agent-avatar' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
						},
					],
					headers: [{ id: 'h-avatar', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'agent-info',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: '_conversation_agent',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: '_conversation_agent',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							// PD-16920 — the Workd template the agent was made from.
							id: 'template-badge',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'templateKey',
									objectTable: '_conversation_agent',
								},
							},
							customComponent: { id: 'conversation-agent-template-badge' },
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'agent-type',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'agent_type',
									objectTable: '_conversation_agent',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: AGENT_TYPE_VALUE_MAPPING,
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: '_conversation_agent',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Agent' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: '_conversation_agent',
						},
					],
					verticalAlignment: 'top',
				},
				{
					id: 'activity-chart',
					fields: [
						{
							id: 'activity-chart-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'monthlySessionData',
									objectTable: '_conversation_agent',
								},
								defaultValue: '[]',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'conversation-agent-activity-sparkline' },
						},
					],
					headers: [{ id: 'h-activity', text: '12 Month Activity' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'play-action',
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'playAgent',
							},
							dataConfig: { type: 'custom_text', customText: 'Play' },
							styleConfig: {
								size: 'md',
								iconName: 'play',
								variant: 'primary',
								isCircular: true,
								colorRule: { type: 'default' },
							},
							displayStyle: 'button',
						},
						{
							id: 'more-actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'call-action',
									label: 'Test Call',
									iconName: 'phone',
									callbackName: 'callAgent',
								},
								{
									id: 'usage-action',
									label: 'Usage',
									iconName: 'chart-line',
									callbackName: 'viewUsageHistory',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'openEditModal',
								},
								{
									id: 'copy-action',
									label: 'Copy',
									iconName: 'copy',
									callbackName: 'copyAgent',
								},
							],
						},
					],
					headers: [{ id: 'h6', text: 'Actions' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
			],
			description: 'All AI conversation agents in the system',
			defaultSortId: 'column-agent-info-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['agentSearch', 'agentType', 'agentStatus', 'voiceProfile'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/agents/:id',
			objectLabel: 'Agent',
			objectTable: '_conversation_agent',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'agent_type', fieldLabel: 'Type' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{
					fieldName: '_fk_conversation_voice_profile__id',
					fieldLabel: 'Voice Profile ID',
				},
				{ fieldName: 'is_active', fieldLabel: 'Active' },
				{ fieldName: 'conversation_count', fieldLabel: 'Conversations' },
				{ fieldName: 'last_used', fieldLabel: 'Last Used' },
				{ fieldName: '_created_date', fieldLabel: 'Created' },
				{ fieldName: 'fa_icon', fieldLabel: 'Icon' },
				{ fieldName: 'agent_color', fieldLabel: 'Color' },
				{ fieldName: 'prompt', fieldLabel: 'Prompt' },
				{ fieldName: 'monthlySessionData', fieldLabel: 'Monthly Session Data' },
				{ fieldName: 'trend', fieldLabel: 'Trend' },
			],
		},
	],
	tableSorts: [
		{
			id: 'last-used',
			name: 'Last Used',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'last_used',
					objectTable: '_conversation_agent',
				},
			],
			description: 'Sort by most recently used',
		},
		{
			id: 'most-active',
			name: 'Most Active',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'conversation_count',
					objectTable: '_conversation_agent',
				},
			],
			description: 'Sort by conversation count',
		},
		{
			id: 'created',
			name: 'Created Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: '_conversation_agent',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
